import { useTranslation } from 'react-i18next';
import { Loader2, CircleCheck, OctagonX } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ActionButton } from '@/shared/ui/action-button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import { FormSelect } from '@/shared/ui/form';
import { CountryFlag, FormField } from '@/shared/ui';

import { usePreferencesForm } from '../model';

interface PreferencesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PreferencesModal = ({ open, onOpenChange }: PreferencesModalProps) => {
  const { t } = useTranslation();

  const { form, onSubmit, alertState, isLoading, availableLanguages } = usePreferencesForm({ open });

  const {
    control,
    handleSubmit,
    formState: { isDirty }
  } = form;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t('pref.title.label')}</DialogTitle>
        </DialogHeader>

        {alertState && (
          <Alert variant={alertState.type === 'error' ? 'destructive' : 'success'} className="mb-4">
            {alertState.type === 'error' ? <OctagonX className="h-4 w-4" /> : <CircleCheck className="h-4 w-4" />}
            <AlertTitle>{t(alertState.type === 'error' ? 'common.error' : 'common.success')}</AlertTitle>
            <AlertDescription>{alertState.message}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="py-2">
          <FormField label={t('setloc.select.language.label')} name="languageCode">
            <FormSelect
              name="languageCode"
              control={control}
              placeholder={t('language.select')}
              options={availableLanguages.map((lang) => ({
                value: lang.code,
                label: lang.label,
                icon: <CountryFlag country={lang.country} />
              }))}
            />
          </FormField>
        </form>

        <DialogFooter>
          <ActionButton intent="dismiss" onClick={() => onOpenChange(false)}>
            {t('gen.closedialog.label')}
          </ActionButton>
          <ActionButton intent="primary" onClick={handleSubmit(onSubmit)} disabled={isLoading || !isDirty}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('newcmr.save.label')}
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
